import * as React from 'react'
import { useEffect, useRef, useState, useMemo } from 'react'

import { useDepositsList } from './hooks'
import {
  Client,
  Deposit,
  ETransactionTypes,
  Gateway,
  Manager,
  TransactionType,
} from './types'
import {
  Button,
  InputRef,
  Table,
  Tag,
  Tooltip,
  Typography,
  Flex,
  message,
} from 'antd'
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons'
import { path } from '../../../../libs'
import { FilterValue } from 'antd/es/table/interface'
import { ColumnsType } from 'antd/lib/table'
import {
  colorTransactionRequestStatus,
  humanTransactionRequestStatus,
  transactionRequestStatus,
  currencyFilterList,
} from '../../const'
import {
  getColumnSearchDateProps,
  getColumnSearchProps,
} from '../../../../libs/tableFunctions'
import { TransactionDepositTypeFilter } from './constants'
import { products } from '../../../../libs/products'
import { Guard } from '../../../../features/session/guard'
import { useSearchParamsInit } from '../../../../libs/useSearchParamsInit'
import { formatDigits } from '../../../../libs/utils'

const { Text } = Typography

const defaultStatusFilters = [
  'TRANSACTION_REQUEST_STATUS_INTERNAL_ERROR',
  'TRANSACTION_REQUEST_STATUS_PENDING_MANAGER',
  'TRANSACTION_REQUEST_STATUS_VENDOR_ERROR',
]

export const DepositList = () => {
  const { initialSearchText, initialFilters, hasUrlFilters } =
    useSearchParamsInit({
      searchTextKeys: ['clientId'],
      filterKeys: ['status'],
    })

  const { deposits, totalCountOfDeposits, loading, onChangeDepositsList } =
    useDepositsList()

  const [searchText, setSearchText] = useState<{ [key: string]: string }>(initialSearchText)
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({
    ...(hasUrlFilters
      ? (initialFilters.status && { status: initialFilters.status })
      : { status: defaultStatusFilters }),
  })

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) setPage(currentPage)
    if (currentPageSize) setPageSize(currentPageSize)
    if (currentOrderBy) setOrderBy(currentOrderBy)
    if (currentOrderDirection)
      setOrderDirection(currentOrderDirection === 'descend' ? '-' : '')
    if (filters) setFiltersState(filters)
  }

  useEffect(() => {
    onChangeDepositsList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, searchText, orderDirection, filtersState])

  const csvCode = useMemo(() => {
    const csvContent = deposits
      .map((item) => Object.values(item).join(','))
      .join('\n')
    return 'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)
  }, [deposits])

  const cell: React.CSSProperties = { fontSize: 18 }
  const mono: React.CSSProperties = { fontSize: 18, fontWeight: 500 }
  const monoLink: React.CSSProperties = { fontSize: 18, fontWeight: 600 }
  const secondary: React.CSSProperties = { fontSize: 11, color: 'var(--crm-text-secondary)' }

  const columns: ColumnsType<Deposit> = [
    {
      title: 'ID',
      dataIndex: 'transactionId',
      width: 90,
      ...getColumnSearchProps<Deposit>(
        'transactionId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: true,
      defaultSortOrder: 'descend',
      render: (v: string) => (
        <Flex align="center" gap={4}>
          <a target="_blank" href={path.billing.deposit.edit(v)} style={monoLink}>{v}</a>
          <CopyOutlined
            style={{ fontSize: 14, color: 'var(--crm-text-secondary)', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(v); message.success(`ID ${v} copied`) }}
          />
        </Flex>
      ),
    },
    {
      title: 'Client',
      key: 'client',
      width: 200,
      children: [
        {
          title: 'Name',
          dataIndex: 'client',
          key: 'client.fullName',
          width: 200,
          ...getColumnSearchProps<Deposit>(
            'client.fullName',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Client) => (
            <Text style={cell}>{item.fullName}</Text>
          ),
        },
        {
          title: 'ID',
          dataIndex: 'client',
          key: 'clientId',
          width: 90,
          ...getColumnSearchProps<Deposit>(
            'clientId',
            setSearchText,
            searchText,
            searchInput,
          ),
          ...(initialSearchText.clientId && {
            defaultFilteredValue: [initialSearchText.clientId],
          }),
          render: (item: Client) => (
            <div>
              <a href={path.clients.details(item.clientId)} target="_blank" style={monoLink}>
                {item.clientId}
              </a>
              {item?.isTest && (
                <Tag color="purple" style={{ fontSize: 9, marginLeft: 4 }}>Test</Tag>
              )}
              {item?.isPremium && (
                <Tooltip title="Premium">
                  <Tag color="gold" style={{ fontSize: 9, marginLeft: 2 }}>👑</Tag>
                </Tooltip>
              )}
            </div>
          ),
        },
      ],
    },
    {
      title: 'Country',
      key: 'country',
      width: 70,
      render: (_: any, r: Deposit) =>
        r.client?.country ? (
          <Tooltip title={r.client.country}>
            <Text style={mono}>{r.client.country}</Text>
          </Tooltip>
        ) : null,
    },
    {
      title: 'Type',
      dataIndex: 'isAuto',
      width: 50,
      filterMultiple: false,
      filters: [
        { text: 'Auto', value: 'true' },
        { text: 'Manual', value: 'false' },
      ],
      render: (item: boolean) => (
        <Tag color={item ? '#2eb85c' : '#321fdb'} style={{ fontSize: 10 }}>
          {item ? 'A' : 'M'}
        </Tag>
      ),
    },
    {
      title: 'Tx type',
      dataIndex: 'transactionType',
      width: 100,
      filters: TransactionDepositTypeFilter,
      render: (item: TransactionType) => (
        <Text style={cell}>{ETransactionTypes[item]}</Text>
      ),
    },
    {
      title: 'Payment system',
      key: 'ps',
      children: [
        {
          title: 'Provider',
          width: 100,
          ...getColumnSearchProps<Deposit>(
            'processingProvider',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Deposit) => (
            <Text style={cell}>{item?.processingProvider}</Text>
          ),
        },
        {
          title: 'Method',
          dataIndex: 'gateway',
          width: 200,
          ...getColumnSearchProps<Deposit>(
            'gateway.internalName',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Gateway) => (
            <Text style={cell}>{item?.internalName}</Text>
          ),
        },
      ],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      render: (item: string) => (
        <Tag color={colorTransactionRequestStatus(item)} style={{ fontSize: 12 }}>
          {humanTransactionRequestStatus(item)}
        </Tag>
      ),
      defaultFilteredValue: initialFilters.status ??
        (hasUrlFilters ? undefined : defaultStatusFilters),
      filters: [
        transactionRequestStatus.new(),
        transactionRequestStatus.pendingManager(),
        transactionRequestStatus.pendingDestination(),
        transactionRequestStatus.ps(),
        transactionRequestStatus.accepted(),
        transactionRequestStatus.internalError(),
        transactionRequestStatus.rejected(),
        transactionRequestStatus.pendingRollback(),
        transactionRequestStatus.unconfirmed(),
        transactionRequestStatus.vendorError(),
      ].map((s) => ({
        text: humanTransactionRequestStatus(s),
        value: s,
      })),
      filterSearch: true,
      onFilter: (value: any, record) => record.status == value,
    },
    {
      title: 'TID',
      width: 100,
      ...getColumnSearchProps<Deposit>(
        'externalTransaction.externalId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: Deposit) => {
        const tid =
          item?.externalTransaction?.externalId ||
          item?.externalPaymentTransaction?.externalId
        return tid ? (
          <Text style={mono}>{tid}</Text>
        ) : null
      },
    },
    {
      title: 'Initial',
      key: 'initial',
      children: [
        {
          title: 'Amount',
          key: 'initialAmount',
          width: 110,
          sorter: true,
          render: (_: any, r: Deposit) => (
            <Text style={mono}>{formatDigits(Number(r.initialAmount))}</Text>
          ),
        },
        {
          title: 'Currency',
          key: 'initialCurrency',
          width: 60,
          filters: currencyFilterList,
          filterSearch: true,
          onFilter: (value: any, record) => record.initialCurrency == value,
          render: (_: any, r: Deposit) => (
            <Text style={mono}>{r.initialCurrency}</Text>
          ),
        },
      ],
    },
    {
      title: 'Client',
      key: 'clientAmountGroup',
      children: [
        {
          title: 'Amount',
          key: 'clientAmount',
          width: 110,
          sorter: true,
          render: (_: any, r: Deposit) => (
            <Text style={mono}>{formatDigits(Number(r.clientAmount))}</Text>
          ),
        },
        {
          title: 'Currency',
          key: 'clientCurrency',
          width: 60,
          filters: currencyFilterList,
          filterSearch: true,
          onFilter: (value: any, record) => record.clientCurrency == value,
          render: (_: any, r: Deposit) => (
            <Text style={mono}>{r.clientCurrency}</Text>
          ),
        },
      ],
    },
    {
      title: 'Dates',
      key: 'dates',
      width: 180,
      ...getColumnSearchDateProps<Deposit>(
        'createdAt',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (_: any, r: Deposit) => (
        <div style={{ ...mono, fontSize: 18, lineHeight: 1.6, whiteSpace: 'nowrap' }}>
          <div><span style={secondary}>C </span>{r.humanCreatedAt || r.createdAt}</div>
          <div><span style={secondary}>U </span>{r.humanUpdatedAt || r.updatedAt}</div>
        </div>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'product',
      width: 90,
      filters: products,
      render: (v: string) => (
        <Tag
          color={v === 'headway' ? '#dbb454' : '#50f9ac'}
          style={{ fontSize: 10, color: '#000', textTransform: 'capitalize' }}
        >
          {v}
        </Tag>
      ),
    },
    {
      title: 'Commission',
      key: 'commission',
      width: 150,
      render: (_: any, r: Deposit) => {
        const client = Number(r.clientCommission)
        const company = Number(r.companyCommission)
        const cur = r.commissionCurrency
        return (
          <div style={{ fontSize: 18, lineHeight: 1.6 }}>
            <div>
              <Text type="secondary" style={{ fontSize: 10 }}>Client: </Text>
              {formatDigits(client)} {cur}
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 10 }}>Company: </Text>
              {formatDigits(company)} {cur}
            </div>
          </div>
        )
      },
    },
    {
      title: 'Manager',
      dataIndex: 'changeHistory',
      key: 'changeHistory.user.email',
      width: 140,
      render: (item: Manager) => (
        <Text style={cell}>{item?.user?.email}</Text>
      ),
    },
  ]

  return (
    <div>
      {/* CSV export */}

      <Flex justify="flex-end" style={{ marginBottom: 10 }} gap="small">
        <Button
          size="small"
          onClick={() =>
            onChangeDepositsList({
              limit: pageSize,
              page,
              orderBy,
              orderDirection,
              searchText,
              filters: filtersState,
            })
          }
        >
          Update Data
        </Button>
        <Guard permission="export_transactions">
          <Button
            size="small"
            icon={<DownloadOutlined />}
            href={csvCode}
            download="deposits.csv"
            target="_blank"
          >
            CSV
          </Button>
        </Guard>
      </Flex>

      {/* Table */}
      <div
        style={{
          background: 'var(--crm-bg)',
          border: '1px solid var(--crm-border)',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <Table
          dataSource={deposits}
          columns={columns}
          size="small"
          scroll={{ x: 1400 }}
          loading={loading}
          rowKey="transactionId"
          pagination={{
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: [15, 30, 50, 100],
            total: totalCountOfDeposits,
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
          }}
          onChange={(pagination, filters, sorter) =>
            handleTableChange({
              // @ts-ignore
              currentPage: pagination.current,
              // @ts-ignore
              currentPageSize: pagination.pageSize,
              // @ts-ignore
              currentOrderDirection: sorter.order,
              // @ts-ignore
              currentOrderBy: sorter.field,
              filters,
            })
          }
        />
      </div>
    </div>
  )
}
